// client/src/components/Sidebar.js
import {
    Box,
    VStack,
    Button,
    Text,
    Spinner,
    IconButton,
    Input,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useDisclosure,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
    Portal
} from "@chakra-ui/react";
import {
    EditIcon,
    DeleteIcon,
} from "@chakra-ui/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useState, useEffect, useRef, useCallback } from "react";
import { fetchWithAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';
const NAVBAR_HEIGHT = 65;

export default function Sidebar({ isOpen, onNewProject, onSelectProject, activeProjectId }) {
    const [projects, setProjects] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);

    // Rename state
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const renameDebounceRef = useRef(null);

    // Delete confirmation dialog
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [deletingProject, setDeletingProject] = useState(null);
    const cancelDeleteRef = useRef();

    // Rename dialog
    const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();
    const [renamingProject, setRenamingProject] = useState(null);
    const [renameInputValue, setRenameInputValue] = useState('');
    const cancelRenameRef = useRef();

    // ── Fetch initial 10 projects ──────────────────────────────────
    const fetchProjects = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetchWithAuth(`${API}/api/projects`, {
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);

            setProjects(data.projects);
            setHasMore(data.hasMore);
            setNextCursor(data.nextCursor);
        } catch (err) {
            console.error('Failed to fetch projects:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch on sidebar open
    useEffect(() => {
        if (isOpen) fetchProjects();
    }, [isOpen, fetchProjects]);

    // ── Load more ───────────────────────────────────
    const loadMore = async () => {
        if (!hasMore || isLoadingMore || !nextCursor) return;
        setIsLoadingMore(true);
        try {
            const res = await fetchWithAuth(`${API}/api/projects?cursor=${nextCursor}`, {
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);

            setProjects(prev => [...prev, ...data.projects]); // append
            setHasMore(data.hasMore);
            setNextCursor(data.nextCursor);
        } catch (err) {
            console.error('Failed to load more projects:', err);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const openRenameDialog = (e, project) => {
        e.stopPropagation();
        setRenamingProject(project);
        setRenameInputValue(project.name || '');
        onRenameOpen();
    };

    const handleRenameConfirm = async () => {
        if (!renamingProject || !renameInputValue.trim()) return;
        try {
            const res = await fetchWithAuth(`${API}/api/projects/${renamingProject.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: renameInputValue.trim() }),
            });
            if (!res.ok) throw new Error('Rename failed');
            setProjects(prev =>
                prev.map(p => p.id === renamingProject.id ? { ...p, name: renameInputValue.trim() } : p)
            );
        } catch (err) {
            console.error('Rename failed:', err);
        } finally {
            onRenameClose();
            setRenamingProject(null);
            setRenameInputValue('');
        }
    };

    const commitRename = () => {
        if (renameDebounceRef.current) clearTimeout(renameDebounceRef.current);
        if (editingName.trim()) {
            // Final save if debounce hasn't fired yet
            fetchWithAuth(`${API}/api/projects/${editingId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: editingName.trim() }),
            }).catch(err => console.error('Final rename failed:', err));

            setProjects(prev =>
                prev.map(p => p.id === editingId ? { ...p, name: editingName.trim() } : p)
            );
        }
        setEditingId(null);
        setEditingName('');
    };

    const cancelRename = (e) => {
        e?.stopPropagation();
        if (renameDebounceRef.current) clearTimeout(renameDebounceRef.current);
        setEditingId(null);
        setEditingName('');
    };

    // ── Delete ─────────────────────────────────────────────────────
    const confirmDelete = (e, project) => {
        e.stopPropagation();
        setDeletingProject(project);
        onDeleteOpen();
    };

    const handleDelete = async () => {
        if (!deletingProject) return;
        try {
            const res = await fetchWithAuth(`${API}/api/projects/${deletingProject.id}`, {
                method: 'DELETE',
            });
            if (!res.ok) throw new Error('Delete failed');

            // Remove from local state — no refetch needed
            setProjects(prev => prev.filter(p => p.id !== deletingProject.id));

            // If deleted project was active, trigger new project
            if (deletingProject.id === activeProjectId) {
                onNewProject();
            }
        } catch (err) {
            console.error('Delete failed:', err);
        } finally {
            onDeleteClose();
            setDeletingProject(null);
        }
    };

    return (
        <>
            <Box
                position="fixed"
                top={`${NAVBAR_HEIGHT}px`}
                left="0"
                height={`calc(100vh - ${NAVBAR_HEIGHT}px)`}
                width="250px"
                bg="white"
                borderRight="1px solid transparent"
                zIndex="900"
                transform={isOpen ? "translateX(0)" : "translateX(-100%)"}
                transition="transform 0.25s ease-out"
                p="20px"
            //     display="flex"
            // flexDirection="column"
            // p="16px"
            // overflowY="auto"
            >
                <VStack align="start" spacing={5}>
                    <Button
                        width="100%"
                        bg="#CF008A"
                        color="#FFFFFF"
                        borderRadius="12px"
                        _hover={{ bg: "#CF008A" }}
                        _active={{ bg: "#CF008A" }}
                        onClick={onNewProject}
                    >
                        New Chat
                    </Button>
                    <Text
                        mt={4}
                        color="#242424"
                        textTransform="uppercase"
                        fontSize="11px"
                        fontStyle="normal"
                        fontWeight="700"
                        lineHeight="16px"
                    >
                        Recents
                    </Text>

                    {isLoading ? (
                        <Spinner size="sm" color="#CF008A" alignSelf="center" mt={4} />
                    ) : (
                        <VStack align="start" spacing={2} width="100%">
                            {projects.length === 0 && (
                                <Text fontSize="sm" color="#9E9E9E" px={2} mt={2}>
                                    No projects yet
                                </Text>
                            )}

                            {projects.map((project) => {
                                const isActive = project.id === activeProjectId;
                                const isEditing = editingId === project.id;
                                return (
                                    <Box
                                        key={project.id}
                                        width="100%"
                                        borderRadius="12px"
                                        border={isActive ? "1px solid #F9C8EA" : "1px solid transparent"}
                                        px={3}
                                        py={2}
                                        color="#4F4F4F"
                                        cursor="pointer"
                                        role="group"
                                        onClick={(e) => {
                                            e.stopPropagation();             // prevent parent toggle
                                            if (!isEditing) onSelectProject(project.id, project.name);
                                        }}
                                        bg={
                                            openMenuId === project.id
                                                ? (isActive ? "#FFF0FA" : "#F7F7F7")  // menu open: hover bg
                                                : isActive
                                                    ? "#FFF0FA"                            // active project: always highlighted
                                                    : "transparent"                        // default: no bg
                                        }
                                        _hover={{ bg: isActive ? "#FFF0FA" : "#F7F7F7" }}
                                        transition="background 0.15s ease"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        //justifyContent="flex-start"
                                        minH="36px"
                                    >
                                        <>
                                            <Text
                                                fontSize="sm"
                                                color={isActive ? "#CF008A" : "#4F4F4F"}
                                                fontWeight={isActive ? "600" : "400"}
                                                noOfLines={1}
                                                flex={1}
                                                mr={1}
                                            >

                                                {project.name}
                                            </Text>

                                            {/* Action buttons — visible on hover */}
                                            <Box
                                                display="flex"
                                                gap={0.5}
                                                opacity={openMenuId === project.id ? 1 : 0}
                                                _groupHover={{ opacity: 1 }}
                                                transition="opacity 0.15s ease"
                                            >
                                                <Menu placement="bottom-end" isLazy

                                                    onOpen={() => setOpenMenuId(project.id)}
                                                    onClose={() => setOpenMenuId(null)}
                                                    closeOnBlur={true}
                                                    strategy="fixed">

                                                    <MenuButton
                                                        as={IconButton}
                                                        icon={<BsThreeDotsVertical
                                                        />}
                                                        size="xs"
                                                        bg="transparent"
                                                        borderRadius="8px"
                                                        boxSize="28px"
                                                        color="#CF008A"
                                                        border="1px solid transparent"
                                                        _hover={{ color: "#CF008A", bg: "transparent" }}
                                                        _active={{ color: "#CF008A", bg: "transparent" }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        aria-label="Project actions"
                                                    />
                                                    <Portal>
                                                        <MenuList
                                                            borderRadius="14px"
                                                            border="1px solid #F9C8EA"
                                                            bg="white"
                                                            // boxShadow="lg"
                                                            boxShadow="0 4px 18px rgba(0,0,0,0.06)"
                                                            p="6px"
                                                            minW="160px"
                                                            // popper props pass-through:
                                                            placement="bottom-end"
                                                            transformOrigin="top right"
                                                            // add small offset via sx if needed
                                                            sx={{ mt: "6px" }}
                                                        >
                                                            <MenuItem
                                                                icon={<EditIcon fontSize="14px" />}
                                                                borderRadius="10px"
                                                                color="gray.700"
                                                                _hover={{ bg: "#FFF0FA", color: "#CF008A" }}
                                                                _focus={{ bg: "#FFF0FA", color: "#CF008A" }}
                                                                _active={{ bg: "#FFF0FA", color: "#CF008A" }}
                                                                onClick={(e) => { e.stopPropagation(); openRenameDialog(e, project); }}
                                                            >
                                                                Rename
                                                            </MenuItem>

                                                            <MenuItem
                                                                icon={<DeleteIcon fontSize="14px" />}
                                                                borderRadius="10px"
                                                                color="gray.700"
                                                                _hover={{ bg: "#FFF0FA", color: "#CF008A" }}
                                                                _focus={{ bg: "#FFF0FA", color: "#CF008A" }}
                                                                _active={{ bg: "#FFF0FA", color: "#CF008A" }}
                                                                onClick={(e) => { e.stopPropagation(); confirmDelete(e, project); }}
                                                            >
                                                                Delete
                                                            </MenuItem>
                                                        </MenuList>
                                                    </Portal>
                                                </Menu>
                                            </Box>
                                        </>
                                    </Box>
                                );
                            })}

                            {/* Load More */}
                            {hasMore && (
                                <Button
                                    width="100%"
                                    variant="ghost"
                                    size="sm"
                                    color="#9E9E9E"
                                    borderRadius="12px"
                                    mt={2}
                                    isLoading={isLoadingMore}
                                    onClick={loadMore}
                                    _hover={{ bg: "#F7F7F7", color: "#4F4F4F" }}
                                >
                                    Load more
                                </Button>
                            )}
                        </VStack>
                    )}
                </VStack>
            </Box >

            {/* Delete Confirmation Dialog */}
            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelDeleteRef}
                onClose={onDeleteClose}
                isCentered
            >
                <AlertDialogOverlay />
                <AlertDialogContent borderRadius="16px" border="1px solid #F9C8EA">
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                        Delete Project
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        Are you sure you want to delete <strong>{deletingProject?.name}</strong>?
                        This will permanently delete all messages, suggestions and reports in this project.
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelDeleteRef} onClick={onDeleteClose} borderRadius="12px"
                            px={2}
                            bg="#FFFFFF"
                            color="#037A9B"
                            border="1px solid #00C2E5"
                            fontWeight="600"
                            _hover={{
                                bg: '#ffffff',
                                color: '#CF008A',
                                borderColor: '#FF9CEE',
                            }}
                            _active={{ bg: '#FFFFFF' }}>
                            Cancel
                        </Button>
                        <Button
                            bg="#CF008A"
                            color="white"
                            _hover={{ bg: "#B60079" }}
                            onClick={handleDelete}
                            ml={3}
                            borderRadius="12px"
                        >
                            Delete
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Rename Dialog */}
            <AlertDialog
                isOpen={isRenameOpen}
                leastDestructiveRef={cancelRenameRef}
                onClose={onRenameClose}
                isCentered
            >
                <AlertDialogOverlay />
                <AlertDialogContent borderRadius="16px" border="1px solid #F9C8EA" mx={4}>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                        Rename Project
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        <Input
                            value={renameInputValue}
                            onChange={(e) => setRenameInputValue(e.target.value)}
                            placeholder="Enter new project name"
                            borderRadius="10px"
                            borderColor="#F9C8EA"
                            _focus={{ borderColor: "#CF008A", boxShadow: "0 0 0 1px #CF008A" }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRenameConfirm();
                                if (e.key === 'Escape') onRenameClose();
                            }}
                            autoFocus
                        />
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRenameRef} onClick={onRenameClose} borderRadius="12px"

                            px={2}
                            bg="#FFFFFF"
                            color="#037A9B"
                            border="1px solid #00C2E5"
                            fontWeight="600"
                            _hover={{
                                bg: '#ffffff',
                                color: '#CF008A',
                                borderColor: '#FF9CEE',
                            }}
                            _active={{ bg: '#FFFFFF' }}

                        >
                            Cancel
                        </Button>
                        <Button
                            bg="#CF008A"
                            color="white"
                            _hover={{ bg: "#B60079" }}
                            onClick={handleRenameConfirm}
                            ml={3}
                            borderRadius="12px"
                        >
                            Rename
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
